import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import {
  Loader2,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  Copy,
  Check,
} from "lucide-react";

import { GET_AI_LOG_DETAIL } from "@/graphql/queries/admin";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function LogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { data, loading } = useQuery(GET_AI_LOG_DETAIL, {
    variables: { id },
    skip: !id,
  });

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Nukopijuota");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("lt-LT", {
      dateStyle: "long",
      timeStyle: "medium",
    }).format(date);
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const log = data?.aiLog;

  if (!log) {
    return (
      <div className="space-y-6">
        <Link to="/admin/logs">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Grįžti į sąrašą
          </Button>
        </Link>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Žurnalo įrašas nerastas
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/logs">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Grįžti
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              AI žurnalo įrašas
            </h1>
            <p className="text-sm text-muted-foreground font-mono">{log.id}</p>
          </div>
        </div>
        <Badge
          variant={log.status === "success" ? "default" : "destructive"}
          className={log.status === "success" ? "bg-green-500" : ""}
        >
          {log.status === "success" ? (
            <CheckCircle className="h-3 w-3 mr-1" />
          ) : (
            <XCircle className="h-3 w-3 mr-1" />
          )}
          {log.status === "success" ? "Sėkmingas" : "Klaida"}
        </Badge>
      </div>

      {/* Metadata */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Clock className="h-4 w-4" />
            Data
          </div>
          <div className="font-medium">{formatDate(log.createdAt)}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Clock className="h-4 w-4" />
            Trukmė
          </div>
          <div className="font-medium">{formatDuration(log.durationMs)}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Zap className="h-4 w-4" />
            Tokenai (in/out)
          </div>
          <div className="font-medium">
            {log.promptTokens ?? "-"} / {log.responseTokens ?? "-"}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Modelis</div>
          <div className="font-medium font-mono text-sm">{log.model}</div>
        </Card>
      </div>

      {/* Additional Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informacija</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-sm text-muted-foreground">Operacija</div>
              <div className="font-medium">{log.operation}</div>
            </div>
            {log.sessionId && (
              <div>
                <div className="text-sm text-muted-foreground">Sesijos ID</div>
                <div className="font-mono text-sm">{log.sessionId}</div>
              </div>
            )}
            {log.studentId && (
              <div>
                <div className="text-sm text-muted-foreground">Mokinio ID</div>
                <div className="font-mono text-sm">{log.studentId}</div>
              </div>
            )}
          </div>
          {log.errorMessage && (
            <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                Klaidos pranešimas
              </div>
              <div className="text-sm text-red-700 dark:text-red-300 font-mono">
                {log.errorMessage}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prompt & Response */}
      <Card>
        <CardHeader>
          <CardTitle>Užklausa ir atsakymas</CardTitle>
          <CardDescription>
            Pilnas AI užklausos ir atsakymo turinys
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="prompt">
            <TabsList className="mb-4">
              <TabsTrigger value="prompt">Užklausa (Prompt)</TabsTrigger>
              <TabsTrigger value="response">Atsakymas (Response)</TabsTrigger>
            </TabsList>
            <TabsContent value="prompt">
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => handleCopy(log.promptText, "prompt")}
                >
                  {copiedField === "prompt" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <pre className="p-4 bg-muted rounded-lg overflow-auto max-h-[600px] text-sm font-mono whitespace-pre-wrap">
                  {log.promptText}
                </pre>
              </div>
            </TabsContent>
            <TabsContent value="response">
              {log.responseText ? (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => handleCopy(log.responseText!, "response")}
                  >
                    {copiedField === "response" ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <pre className="p-4 bg-muted rounded-lg overflow-auto max-h-[600px] text-sm font-mono whitespace-pre-wrap">
                    {log.responseText}
                  </pre>
                </div>
              ) : (
                <div className="p-4 text-center text-muted-foreground">
                  Nėra atsakymo (klaida arba tuščias atsakymas)
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
